/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// JS bridge to the native WmhLiveActivityPlugin (iOS ActivityKit). Starts /
// updates / ends the Walk Me Home Live Activity — the lock-screen + Dynamic
// Island trip card. iOS 16.2+ only; every function fails soft (no-op) on
// web / Android / older iOS / when the user disabled Live Activities, so the
// caller keeps its existing persistent-notification fallback with no branching.
//
// The native side does the availability gating; here we just guard the platform
// and swallow errors. `percentComplete` is 0..100 COVERED (the widget shows
// "% left" = 100 − this and fills the bar with covered).

import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";

// "walker" = the user's own trip; "watcher" = a trip they're tracking. Keyed
// separately in native so both can be live at once (B can walk AND watch).
export type LiveActivityRole = "walker" | "watcher";

interface WmhLiveActivityPluginShape {
  isSupported(): Promise<{ supported: boolean }>;
  start(opts: {
    role: LiveActivityRole;
    walkerName: string;
    destinationName: string;
    travelMode: string;
    percentComplete: number;
    etaMinutes?: number;
  }): Promise<{ started: boolean; id?: string; reason?: string }>;
  update(opts: {
    role: LiveActivityRole;
    percentComplete: number;
    etaMinutes?: number;
    status: string;
  }): Promise<{ updated: boolean; reason?: string }>;
  end(opts: {
    role: LiveActivityRole;
    status: string;
  }): Promise<{ ended: boolean; reason?: string }>;
  addListener(
    event: "pushToken",
    cb: (data: { role: LiveActivityRole; token: string }) => void,
  ): Promise<PluginListenerHandle>;
}

const WmhLiveActivity = registerPlugin<WmhLiveActivityPluginShape>(
  "WmhLiveActivity",
);

function isIos(): boolean {
  return Capacitor.getPlatform() === "ios";
}

/** True only on iOS where Live Activities are available AND enabled. */
export async function liveActivitySupported(): Promise<boolean> {
  if (!isIos()) return false;
  try {
    const r = await WmhLiveActivity.isSupported();
    return r.supported === true;
  } catch {
    return false;
  }
}

export async function startWmhLiveActivity(opts: {
  role?: LiveActivityRole;
  walkerName: string;
  destinationName: string;
  travelMode: string;
  percentComplete: number;
  etaMinutes?: number;
}): Promise<boolean> {
  if (!isIos()) return false;
  try {
    const r = await WmhLiveActivity.start({
      role: opts.role ?? "walker",
      walkerName: opts.walkerName,
      destinationName: opts.destinationName,
      travelMode: opts.travelMode,
      percentComplete: opts.percentComplete,
      etaMinutes: opts.etaMinutes,
    });
    return r.started === true;
  } catch (err) {
    console.warn("[liveActivity] start failed:", err);
    return false;
  }
}

export async function updateWmhLiveActivity(opts: {
  role?: LiveActivityRole;
  percentComplete: number;
  etaMinutes?: number;
  status: string;
}): Promise<void> {
  if (!isIos()) return;
  try {
    await WmhLiveActivity.update({
      role: opts.role ?? "walker",
      percentComplete: opts.percentComplete,
      etaMinutes: opts.etaMinutes,
      status: opts.status,
    });
  } catch (err) {
    console.warn("[liveActivity] update failed:", err);
  }
}

export async function endWmhLiveActivity(
  status: string,
  role: LiveActivityRole = "walker",
): Promise<void> {
  if (!isIos()) return;
  try {
    await WmhLiveActivity.end({ role, status });
  } catch (err) {
    console.warn("[liveActivity] end failed:", err);
  }
}

/**
 * Subscribe to the activity's APNs push token (emitted by native on start + on
 * rotation). The caller writes it to RTDB so the server push function can update
 * the card while the app is suspended/locked. Returns a handle to remove the
 * listener; no-op (null handle) off iOS.
 */
export async function onWmhPushToken(
  cb: (role: LiveActivityRole, token: string) => void,
): Promise<PluginListenerHandle | null> {
  if (!isIos()) return null;
  try {
    return await WmhLiveActivity.addListener("pushToken", (data) =>
      cb(data.role, data.token),
    );
  } catch (err) {
    console.warn("[liveActivity] addListener(pushToken) failed:", err);
    return null;
  }
}
